import { useState } from "react";
import { Box, Button, TextField, Typography, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import "../../styles/header.css";
import BASE_URL from "../../components/config/appconfig";
import SnackbarAlert from "../../components/snackbarAlert/snackbarAlert";
import usePageMeta from "../../utils/usePageMeta";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

export default function SetPassword() {
  usePageMeta(
    "Set Your Password",
    "Set a password for your Gospel and Church Health Framework account.",
  );

  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value.trim() && !validateEmail(value.trim())) {
      setErrors("Enter a valid email address");
    } else {
      setErrors("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setErrors("Email is required");
      return;
    }
    if (!validateEmail(email.trim())) {
      setErrors("Enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/admin/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      const result = await response.json();

      if (result.success) {
        setSent(true);
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to send link. Please try again.",
          severity: "error",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Page title */}
      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          display: "flex",
          justifyContent: "center",
          px: { xs: 2, sm: 4 },
        }}
      >
        <Typography
          variant="h3"
          className="title"
          sx={{
            fontSize: {
              xs: "20px !important",
              sm: "28px !important",
              md: "32px !important",
            },
            textAlign: "center",
          }}
        >
          The Gospel and Church Health Framework
        </Typography>
      </Box>

      <Box
        sx={{
          maxWidth: 500,
          mx: "auto",
          p: { xs: 2, sm: 4, md: 5 },
          borderRadius: 2,
        }}
      >
        {sent ? (
          /* ── Success state ── */
          <Box sx={{ textAlign: "center" }}>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 52, color: "#20A15C", mb: 2 }}
            />
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "18px" },
                fontWeight: 700,
                color: "#1a202c",
                mb: 1.5,
              }}
            >
              Check your inbox
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "12px", sm: "14px" },
                color: "#666",
                mb: 3,
                lineHeight: 1.7,
              }}
            >
              We've sent a password setup link to <strong>{email}</strong>.
              Click the link in that email to change your password and activate
              your account.
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "11px", sm: "13px" },
                color: "#999",
                mb: 3,
              }}
            >
              Didn't receive it? Check your spam folder or{" "}
              <span
                onClick={() => {
                  setSent(false);
                  setEmail("");
                }}
                style={{
                  color: "#263842",
                  fontWeight: 600,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                try again
              </span>
              .
            </Typography>
            <Link
              component={RouterLink}
              to="/sign-in"
              underline="hover"
              sx={{
                fontSize: { xs: 13, sm: 14 },
                color: "#263842",
                fontWeight: 600,
              }}
            >
              Back to Sign In
            </Link>
          </Box>
        ) : (
          /* ── Form state ── */
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Typography
              sx={{
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: 700,
                color: "#1a202c",
                mb: 1,
                textAlign: "center",
              }}
            >
              Set Your Password
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "12px", sm: "14px" },
                color: "#666",
                mb: 3,
                textAlign: "center",
                lineHeight: 1.7,
              }}
            >
              Welcome to the Gospel and Church Health Framework. Enter the email
              address you registered with and we'll send you a link to set your
              password.
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "12px", sm: "13px" },
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              Email Address
            </Typography>
            <TextField
              fullWidth
              required
              placeholder="Enter your registered email"
              type="email"
              value={email}
              onChange={handleChange}
              error={!!errors}
              helperText={errors}
              size="small"
              disabled={loading}
              autoFocus
            />

            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !!errors || !email.trim()}
                className={
                  !loading && !errors && email.trim()
                    ? "next-button"
                    : "next-button-disabled"
                }
                sx={{
                  width: { xs: "100%", sm: 160 },
                  height: 48,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                {loading ? "Sending..." : "Send Setup Link"}
              </Button>
            </Box>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Link
                component={RouterLink}
                to="/sign-in"
                underline="hover"
                sx={{
                  fontSize: { xs: 13, sm: 14 },
                  color: "#263842",
                  fontWeight: 600,
                }}
              >
                Back to Sign In
              </Link>
            </Box>
          </Box>
        )}
      </Box>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}
